import rawShows from "../data/shows.json" with { type: "json" };
import { heroAssetRegistry } from "./heroAssetRegistry.js";
import { getEstimatedFilmingDate } from "./dateUtils.js";
import { getShowDefaultFilmingOffsetMonths } from "./filmingOffsetProfiles.js";

// Keep this main-page sequence stable. New shows should be appended
// after these unless the user explicitly asks to change the order.
const MAIN_PAGE_SHOW_ORDER = [
  "seinfeld",
  "the-office",
  "parks-and-recreation",
  "friends",
  "game-of-thrones",
  "the-sopranos",
  "modern-family",
  "arrested-development",
  "brooklyn-nine-nine",
  "its-always-sunny",
  "broad-city",
  "high-maintenance",
];

function sortEpisodesByAirDate(episodes) {
  return [...episodes].sort((left, right) =>
    left.airDate.localeCompare(right.airDate),
  );
}

function sortShowsForHome(shows) {
  const order = new Map(
    MAIN_PAGE_SHOW_ORDER.map((showId, index) => [showId, index]),
  );

  return shows
    .map((show, index) => ({ show, index }))
    .sort((left, right) => {
      const leftRank = order.get(left.show.id) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = order.get(right.show.id) ?? Number.MAX_SAFE_INTEGER;

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return left.index - right.index;
    })
    .map(({ show }) => show);
}

function buildShowRecord(show) {
  const actorsById = new Map(show.actors.map((actor) => [actor.id, actor]));
  const hero = heroAssetRegistry[show.image];
  const homeHero = show.homeImage ? heroAssetRegistry[show.homeImage] : null;

  return {
    ...show,
    hero,
    homeHero,
    actorsById,
    episodes: sortEpisodesByAirDate(show.episodes),
  };
}

export function createDataStore() {
  const shows = sortShowsForHome(rawShows.shows.map(buildShowRecord));
  const showsById = new Map(shows.map((show) => [show.id, show]));

  function mergeShowCredits(entries) {
    const byCreditKey = new Map();

    entries.forEach((entry) => {
      const creditKey = [
        entry.showId,
        entry.title ?? "",
        entry.creator ?? "",
        entry.source ?? "",
        entry.license ?? "",
        entry.licenseUrl ?? "",
        entry.notes ?? "",
      ].join("|");

      if (!byCreditKey.has(creditKey)) {
        byCreditKey.set(creditKey, {
          ...entry,
          assetUsage: entry.assetUsage ? [entry.assetUsage] : [],
        });
        return;
      }

      const existing = byCreditKey.get(creditKey);
      if (entry.assetUsage && !existing.assetUsage.includes(entry.assetUsage)) {
        existing.assetUsage.push(entry.assetUsage);
      }
    });

    return [...byCreditKey.values()].map((entry) => ({
      ...entry,
      assetUsage: entry.assetUsage.join(" + "),
    }));
  }

  return {
    shows,
    getShows() {
      return shows;
    },
    getShowById(id) {
      return showsById.get(id) ?? null;
    },
    getEpisode(showId, episodeId) {
      const show = showsById.get(showId);
      if (!show) {
        return null;
      }

      const episode =
        show.episodes.find((item) => item.id === episodeId) ?? null;
      if (!episode) {
        return null;
      }

      return { show, episode };
    },
    getHeroCredits() {
      return shows.flatMap((show) => {
        const credits = [
          {
            showId: show.id,
            showTitle: show.title,
            assetUsage: "Show page hero",
            ...show.hero.credit,
          },
        ];

        if (show.homeHero) {
          credits.push({
            showId: show.id,
            showTitle: show.title,
            assetUsage: "Home page card / mobile show hero",
            ...show.homeHero.credit,
          });
        }

        return mergeShowCredits(credits);
      });
    },
  };
}

export function getPilotEpisode(show) {
  return show.episodes[0] ?? null;
}

function normalizeEpisodeArgs(showOrEpisode, maybeEpisode) {
  if (maybeEpisode) {
    return { show: showOrEpisode, episode: maybeEpisode };
  }

  return { show: null, episode: showOrEpisode };
}

export function getEpisodeOffsetMonths(showOrEpisode, maybeEpisode) {
  const { show, episode } = normalizeEpisodeArgs(showOrEpisode, maybeEpisode);
  return (
    episode.estimatedFilmOffsetMonths ??
    getShowDefaultFilmingOffsetMonths(show, episode.season)
  );
}

export function getEpisodeFilmingDate(showOrEpisode, maybeEpisode) {
  const { show, episode } = normalizeEpisodeArgs(showOrEpisode, maybeEpisode);
  return getEstimatedFilmingDate(
    episode.airDate,
    getEpisodeOffsetMonths(show, episode),
  );
}

export function getEpisodeCast(show, episode) {
  return episode.cast
    .map((actorId) => show.actorsById.get(actorId))
    .filter(Boolean);
}
